import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './all-servers-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import AllServersTable, { ALL_SERVERS_COLUMN_DEFS } from './components/AllServersTable.jsx';
// Shared components from residents (fully generic)
import ActiveFilterBar from '../../community/residents/components/ActiveFilterBar.jsx';
import ColumnVisibilityToggle from '../../community/residents/components/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../../community/residents/components/TableSkeleton.jsx';
// V2 data layer
import { getCampusId, getRoleWeight } from '../../../services/session.js';
import { fetchAllServers } from './api/allServersApi.js';
import { exportAllServersCsv } from '../../../services/csvExport.js';

/** Default visible columns — all 6 */
const DEFAULT_VISIBLE = ALL_SERVERS_COLUMN_DEFS.map((c) => c.key);

/**
 * AllServersPage — V2 read-only, cross-campus server overview.
 *
 * Shows all PalCare servers across every campus in a single table
 * with search, column filters, sort, pagination, column visibility,
 * and CSV export.
 */
const AllServersPage = () => {
  // ==========================================================================
  // SESSION
  // ==========================================================================
  const campusId = useMemo(() => getCampusId(), []);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);

  // ==========================================================================
  // TOAST
  // ==========================================================================
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // DATA STATE
  // ==========================================================================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState('campus_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchAllServers(campusId, true);
      setData(result || []);
    } catch (err) {
      console.error('[AllServersPage] Load error:', err);
      showToast('error', 'Failed to load servers');
    } finally {
      setLoading(false);
    }
  }, [campusId, showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // ==========================================================================
  // FILTER / SORT PIPELINE
  // ==========================================================================
  const filtered = useMemo(() => {
    let result = [...data];

    // 1. Text search
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((r) =>
        r.campus_name?.toLowerCase().includes(s) ||
        r.corporate_name?.toLowerCase().includes(s) ||
        r.checkin_time?.toLowerCase().includes(s) ||
        r.recent_alarm_input_time?.toLowerCase().includes(s) ||
        r.version_code?.toLowerCase().includes(s) ||
        r.version_database?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((row) => {
        const cellVal = row[columnKey];
        return values.includes(cellVal != null ? String(cellVal) : '(empty)');
      });
    });

    // 3. Sort
    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Date sort for datetime columns
      if (sortColumn === 'checkin_time' || sortColumn === 'recent_alarm_input_time') {
        const aDate = aVal ? new Date(aVal).getTime() : 0;
        const bDate = bVal ? new Date(bVal).getTime() : 0;
        const diff = aDate - bDate;
        return sortOrder === 'asc' ? diff : -diff;
      }

      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [data, search, filters, sortColumn, sortOrder]);

  // ==========================================================================
  // PAGINATION
  // ==========================================================================
  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
  const showingEnd = Math.min(safePage * perPage, totalFiltered);

  useEffect(() => { setPage(1); }, [search, filters, perPage]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [safePage, totalPages]);

  /** Column labels for ActiveFilterBar */
  const columnLabels = useMemo(() => {
    const map = {};
    ALL_SERVERS_COLUMN_DEFS.forEach((c) => { map[c.key] = c.label; });
    return map;
  }, []);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleSort = useCallback((key) => {
    setSortColumn((prev) => {
      if (prev === key) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return key;
    });
  }, []);

  const handleFilterChange = useCallback((columnKey, selectedValues) => {
    setFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
  }, []);

  const handleRemoveFilter = useCallback((columnKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const handleColumnToggle = useCallback((columnKey) => {
    setVisibleColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey]
    );
  }, []);

  const handleExportAll = useCallback(() => {
    if (filtered.length === 0) {
      showToast('error', 'No data to export');
      return;
    }
    exportAllServersCsv(filtered, 'all_servers.csv');
    showToast('success', `Exported ${filtered.length} servers`);
  }, [filtered, showToast]);

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="dp-page">
      {/* Page-level toast notification */}
      {toast ? (
        <div
          className={`dp-toast ${toast.type === 'success' ? 'dp-toast--success' : 'dp-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
            className="dp-toast__close"
            aria-label="Dismiss"
          >
            <svg className="dp-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : null}

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="dp-page-header">
        <div className="dp-page-header__inner">
          <div className="dp-breadcrumb-row">
            <div className="dp-breadcrumb">
              <span className="dp-breadcrumb__text">Customers</span>
              <svg className="dp-icon-md dp-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="dp-breadcrumb__current">Servers</span>
            </div>
          </div>
          <h1 className="dp-page-title">All Servers</h1>
        </div>
      </div>

      {/* ============================================================
          CONTENT
          ============================================================ */}
      <div className="dp-content">
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="search"
                placeholder="Search servers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dp-search__input"
                aria-label="Search servers"
              />
            </div>
          </div>
          <div className="dp-controls__right">
            <ColumnVisibilityToggle columns={ALL_SERVERS_COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
            <button type="button" className="dp-btn dp-btn--secondary" onClick={handleExportAll} aria-label="Export servers">
              <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export
            </button>
          </div>
        </div>
        <ActiveFilterBar filters={filters} columnLabels={columnLabels} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />
        {loading ? (
          <TableSkeleton />
        ) : (
          <AllServersTable
            data={paginated}
            allData={data}
            visibleColumns={visibleColumns}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={handleSort}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        )}
        {/* Pagination */}
        <div className="dp-pagination">
          <p className="dp-pagination__info">
            Showing <span className="dp-pagination__emphasis">{showingStart}</span>&ndash;<span className="dp-pagination__emphasis">{showingEnd}</span> of{' '}
            <span className="dp-pagination__emphasis">{totalFiltered}</span> servers
          </p>
          <div className="dp-pagination__controls">
            <div className="dp-per-page">
              <label htmlFor="dp-as-per-page" className="dp-per-page__label">Rows per page:</label>
              <select
                id="dp-as-per-page"
                value={perPage}
                onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                className="dp-per-page__select"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <nav className="dp-page-nav" aria-label="Servers pagination">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="dp-page-btn"
                aria-label="Previous page"
              >
                <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              {pageNumbers.map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setPage(num)}
                  className={`dp-page-num ${num === safePage ? 'is-active' : ''}`}
                  aria-label={`Page ${num}`}
                  aria-current={num === safePage ? 'page' : undefined}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="dp-page-btn"
                aria-label="Next page"
              >
                <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
    </div>
    </>
  );
};

export default AllServersPage;
